import { useMemo } from 'react';
import { compact, get } from 'lodash';
import { assertNotNil, type CustomizableMetric, getBaseFee, hasFeesExcluded } from 'venn-utils';
import { convertToTimeseries, isExportable } from '../../utils';
import { formatExportableNameWithOptionalFee, formatNameWithOptionalFee } from '../../../legend';
import type { Serie } from '../../../studio-blocks/components/charts/types';
import type { Analysis, RollingCorrelationResponse } from 'venn-api';
import type { StudioAnalysisRequest } from 'venn-state';
import { usePageProxySymbol } from '../../components/core/block-subject-footnotes-and-superscripts';

export const useRollingCorrelation = (
  analyses: (Analysis | undefined)[][],
  metric: CustomizableMetric | undefined,
  requests: StudioAnalysisRequest[],
  subjectHasProxy: { [subjectId: string]: boolean | undefined },
): [series: Serie[], exportableSeries: Serie[]] => {
  const proxySymbol = usePageProxySymbol();
  return useMemo(() => {
    if (!analyses || analyses.length < 1 || !metric) {
      return [[], []];
    }

    const series: Serie[] = [];
    const exportableSeries: Serie[] = [];

    const analysis = compact(analyses)[0]!.find((analysis) => analysis?.analysisType === 'ROLLING_CORRELATION');
    const exportable = analysis?.exportable;

    analysis?.rollingCorrelation.forEach((correlation: RollingCorrelationResponse) => {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const ts = metric.metricPath ? get(correlation, metric.metricPath) : correlation[0];
      if (!ts) {
        return;
      }

      // TODO: VENN-24861 Fix subject and subject name logic.
      const primarySubjectName = correlation.subjectName.name; // getSubjectLabelForChart(correlation.subjectName);
      const secondarySubjectName = correlation.secondarySubjectName.name; // getSubjectLabelForChart(correlation.secondarySubjectName);
      const primarySubject = assertNotNil(
        requests.find((request) => {
          return request.subject.name === primarySubjectName;
        }),
      ).subject;
      const secondarySubject = requests.find((request) => {
        return request.subject.name === secondarySubjectName;
      })?.subject;
      const seriesName = `${formatNameWithOptionalFee(
        primarySubjectName,
        proxySymbol,
        getBaseFee(primarySubject),
        hasFeesExcluded(primarySubject),
        !!subjectHasProxy[primarySubject.id],
      )} with ${formatNameWithOptionalFee(
        secondarySubjectName,
        proxySymbol,
        secondarySubject ? getBaseFee(secondarySubject) : undefined,
        secondarySubject ? hasFeesExcluded(secondarySubject) : undefined,
        correlation?.secondarySubjectName?.id ? !!subjectHasProxy[correlation.secondarySubjectName.id] : undefined,
      )}`;
      const exportableSeriesName = `${formatExportableNameWithOptionalFee(
        primarySubjectName,
        getBaseFee(primarySubject),
        hasFeesExcluded(primarySubject),
      )} with ${formatExportableNameWithOptionalFee(
        secondarySubjectName,
        secondarySubject ? getBaseFee(secondarySubject) : undefined,
        secondarySubject ? hasFeesExcluded(secondarySubject) : undefined,
      )}`;
      const seriesData = convertToTimeseries(ts);
      const serie = {
        name: seriesName,
        data: seriesData,
      };
      const exportableSerie = {
        name: exportableSeriesName,
        data: seriesData,
      };
      series.push(serie);

      if (
        isExportable(exportable ?? [], requests, correlation.subjectName) &&
        isExportable(exportable ?? [], requests, correlation.secondarySubjectName)
      ) {
        exportableSeries.push(exportableSerie);
      }
    });
    return [series, exportableSeries];
  }, [analyses, metric, proxySymbol, requests, subjectHasProxy]);
};
